import { Info } from 'lucide-react';
import { driveThumbnailUrl } from '../lib/drive';

/**
 * The ⓘ icon next to a form label. Hovering it (or tapping, on touch — it's
 * focusable) shows a plain-English explanation of the field plus a curated
 * example, so someone creating their first avatar never faces a blank box
 * without knowing what "good" looks like.
 */
export default function FieldHint({
  what,
  example,
  imageUrl,
}: {
  /** What this field is and what the pipeline does with it. */
  what: string;
  /** A real, good example value (curated — adapted from an existing avatar). */
  example?: string;
  /** For photo fields: what a good reference image looks like. */
  imageUrl?: string;
}) {
  return (
    <span className="relative inline-flex group/hint align-middle ml-1.5" tabIndex={0}>
      <Info
        size={13}
        className="text-natural-text/35 group-hover/hint:text-natural-sage group-focus-within/hint:text-natural-sage cursor-help transition-colors"
      />
      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover/hint:block group-focus-within/hint:block z-[70] w-[340px] max-w-[85vw]">
        <span className="block bg-white border border-natural-border rounded-2xl shadow-2xl p-3.5 space-y-2 text-left normal-case font-normal tracking-normal">
          <span className="block text-[11px] text-natural-text/75 leading-relaxed">{what}</span>
          {example && (
            <span className="block text-[11px] leading-relaxed bg-natural-bg border border-natural-border/60 rounded-xl p-2.5 font-mono whitespace-pre-wrap text-natural-text/80">
              <span className="block font-sans font-semibold text-[10px] uppercase tracking-wide text-natural-sage mb-1">
                Example
              </span>
              {example}
            </span>
          )}
          {imageUrl && (
            <img
              src={driveThumbnailUrl(imageUrl, 'w400')}
              alt="Example"
              referrerPolicy="no-referrer"
              loading="lazy"
              className="w-full h-auto max-h-52 object-contain rounded-xl border border-natural-border/60 bg-stone-100"
            />
          )}
        </span>
      </span>
    </span>
  );
}
